import request, { requestServices } from "@/utils/request";
import { getUserAccessToken } from "@/utils/helpers/auth";
import {
  ICheckInConfig,
  IRedeemByBarCodePayload,
  IRedeemByBarCodeResult,
  IBookingTicketHistory,
  IRedeemGroupPayload,
  IRedeemResult,
  ITicketScanHistory,
} from "@/types/check-in";

// requestServices sends no authentication. The engine reads the Cognito
// access token and compares its groups with the groupId in the request.
function bearer() {
  return { Authorization: `Bearer ${getUserAccessToken()}` };
}

/**
 * Get the area that is linked to a POS configuration.
 *
 * @param {number} groupId group of the POS configuration.
 * @param {number} posConfigId POS configuration id.
 * @returns {Promise<ICheckInConfig>} the area, or null fields if there is none.
 */
export async function getPosCheckInConfig(
  groupId: number,
  posConfigId: number
): Promise<ICheckInConfig> {
  const response = await requestServices({
    method: "GET",
    url: "/access-control/pos/config",
    params: { groupId, posConfigId },
    headers: bearer(),
  });

  return response.data.data;
}

/**
 * Check in tickets of one booking. Each ticket gets its own outcome.
 * A ticket that fails does not stop the other tickets.
 *
 * @param {IRedeemGroupPayload} payload the tickets to check in.
 * @returns {Promise<IRedeemResult[]>} one result for each ticket.
 */
export async function redeemGroup(
  payload: IRedeemGroupPayload
): Promise<IRedeemResult[]> {
  const response = await requestServices({
    method: "POST",
    url: "/access-control/pos/redeem-group",
    data: payload,
    headers: bearer(),
  });

  return response.data.data.results || [];
}

/**
 * Check in one ticket from a scanned code.
 *
 * @param {IRedeemByBarCodePayload} payload the scanned code.
 * @returns {Promise<IRedeemByBarCodeResult>} the outcome of that ticket.
 */
export async function redeemByBarCode(
  payload: IRedeemByBarCodePayload
): Promise<IRedeemByBarCodeResult> {
  const response = await requestServices({
    method: "POST",
    url: "/access-control/pos/redeem",
    data: payload,
    headers: bearer(),
  });

  return response.data.data;
}

// Request parameter of the scan endpoint (1 scan, 2 unscan). The scan LOG
// rows number differently (ScanLogAction: 2 scan, 3 unscan); not the same code.
const UNSCAN_ACTION = 2;

/**
 * Take back the entry of one ticket.
 *
 * The access control engine exposes no reversal for a POS bound to an area,
 * so this calls the scan endpoint of the backend (APPS-511).
 *
 * @param {number} productId - Product of the ticket category.
 * @param {number} pssId - The ticket to take back.
 * @returns {Promise<void>}
 */
export async function unscanTicket(
  productId: number,
  pssId: number
): Promise<void> {
  await request({
    method: "POST",
    url: `/product/${productId}/scan/ticket`,
    params: { channel: "POS", ticketId: pssId, action: UNSCAN_ACTION },
  });
}

/**
 * Get the scans of every ticket of one booking.
 *
 * The engine writes its own log on each entry, so this says who is already
 * in even when the ticket row itself was not marked.
 *
 * @param {string} bookingId - The booking to read.
 * @returns {Promise<IBookingTicketHistory[]>} One entry for each ticket.
 */
export async function getBookingScanHistory(
  bookingId: string
): Promise<IBookingTicketHistory[]> {
  const response = await requestServices({
    method: "GET",
    url: "/v1/access-control/log-scan-tickets",
    params: { bookingId },
    headers: bearer(),
  });

  return response.data.data || [];
}

/**
 * Get the scans of one ticket, with the time and the operator.
 * The engine gives one ticket for each call.
 *
 * @param {number} pssId - The ticket to read.
 * @returns {Promise<ITicketScanHistory>} The scans of the ticket.
 */
export async function getTicketScanHistory(
  pssId: number
): Promise<ITicketScanHistory> {
  const response = await requestServices({
    method: "GET",
    url: "/v1/access-control/log-scan-tickets",
    params: { pssId },
    headers: bearer(),
  });

  return response.data.data;
}
